import { Select, selectState as ss } from "@bfc-software/pallet";
import type { SelectMsg, SelectState } from "@bfc-software/pallet";
import clsx from "clsx";
import { useContext, useMemo } from "react";
import { match } from "ts-pattern";
import {
  useParams,
  useNavigate,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import {
  AuthContext,
  AuthUtils,
  AUTHENTICATED,
} from "@bfc-software/app-toolbox";
import {
  useTranslation,
  DEFAULT_LOCALE,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import {
  warehouseSwitchTarget,
  WAREHOUSE_FALLBACK_PATH,
} from "@bfc-software/bfc-web-ui/lib/warehouse-switch-target";

interface LocationSelectorProps {
  className?: string;
}

type Warehouse = ss.Item; // { id: warehouseId, label: locationName }

/**
 * Pure. `null` (render nothing) when there is nothing true to show: no
 * locations, or the URL's id is not one of them (no stand-in row).
 */
function locationSelectState(a: {
  locations: ReadonlyArray<{ warehouseId: string; locationName: string }>;
  currentId: string | undefined;
  label: string;
}): SelectState<Warehouse> | null {
  const [first, ...rest] = a.locations.map((l) => ({
    id: l.warehouseId,
    label: l.locationName,
  }));
  const current = [first, ...rest].find((i) => i?.id === a.currentId);
  if (first === undefined || current === undefined) return null;
  return ss.always({
    label: ss.ariaLabel(a.label),
    options: ss.fixed<Warehouse>([first, ...rest]),
    selected: current,
  });
}

export function LocationSelector({ className }: LocationSelectorProps) {
  const { state } = useContext(AuthContext);
  const { location: locationId, org, lang } = useParams({ strict: false });
  const navigate = useNavigate();

  const orgId = state.status === AUTHENTICATED ? state.orgId : null;
  const locations = useMemo(
    () => (orgId ? AuthUtils.getLocations(state, orgId) : []),
    [state, orgId],
  );

  const { t } = useTranslation({
    lang: lang || DEFAULT_LOCALE,
    namespace: TRANSLATION_NAMESPACE.locationSelector,
  });

  const router = useRouter();
  const matches = useRouterState({ select: (s) => s.matches });

  const targetPath = useMemo(
    () =>
      warehouseSwitchTarget(
        matches[matches.length - 1]?.fullPath,
        (path) => path in router.routesByPath,
      ) as typeof WAREHOUSE_FALLBACK_PATH,
    [matches, router],
  );

  /**
   * Switching warehouse changes the URL, and nothing else.
   *
   * The warehouse layout watches the URL and re-mints the session to match, so
   * this must not also re-mint. Doing both is a loop: one effect moves the URL
   * to the session while the other moves the session to the URL, and each one
   * re-triggers the other, minting a session per pass.
   */
  const onMsg = (msg: SelectMsg<Warehouse>) =>
    match(msg)
      .with({ kind: "picked" }, ({ item }) => {
        if (typeof item.id !== "string" || item.id === locationId) return;
        navigate({
          to: targetPath,
          params: {
            org: org ?? "",
            lang: lang ?? "",
            location: item.id,
          },
          search: (prev: Record<string, unknown>) => prev,
          replace: true,
        });
      })
      // `always`: no `cleared`/`searched`; `opened`/`closed` carry nothing here
      .otherwise(() => {});

  const select = locationSelectState({
    locations,
    currentId: locationId,
    label: t("locationPickerLabel"),
  });
  return (
    select && (
      <Select
        state={select}
        onMsg={onMsg}
        display={{ trigger: { minWidth: "200px" } }}
        className={clsx(className)}
      />
    )
  );
}
